/**
 * AddDogForm — #653. The front-desk "add this walk-in's dog" form.
 *
 * ── The Counter Test (DESIGN-PHILOSOPHY.md) ──────────────────────────
 * At 6:45 AM with a line of wet dogs at the door, a receptionist who
 * started last week has an owner in front of her whose dog isn't in the
 * system. She must be able to get that dog on file without reading
 * anything, and without the owner having to produce paperwork.
 *
 * That shapes every decision here:
 *
 * · TWO required fields — the dog's name (autofocused when the modal
 *   opens) and, since #1116, whether the dog is spayed or neutered. Ted
 *   (09-24): "Keep Spayed/Neutered as a required Yes/No field." HTHD's
 *   agreement needs altered dogs at six months, and a record that accepted
 *   silence here is how his two rescue puppies were assumed fixed. The
 *   question is answerable by asking the owner standing there; "Not sure"
 *   is no longer an answer the form will save. Everything else is
 *   genuinely optional. (Principle 2: the next action is always obvious.)
 * · The five fields staff can answer by LOOKING at the dog (name, breed,
 *   size, colour, altered) are the default view. Vet contact, allergies,
 *   care notes and the rest live behind one "Add more details" toggle —
 *   collapsed, not removed, so a thorough intake is one tap away and a
 *   rushed one is never blocked. (Principle 1: one hero, everything else
 *   behind a tap.)
 * · Vet name/phone are NOT required, deliberately: vet info is optional
 *   when a dog is registered, here and in the owner's app alike. An owner
 *   can choose to require vet name and phone before a dog's first ONLINE
 *   booking (Settings → Operations, SPEC 2026-10-26 Story 5); even then
 *   the desk is never stopped. A walk-in must never be turned away over a
 *   phone number nobody has on them. The panel says so in plain words,
 *   and the saved record comes back FLAGGED incomplete — the honest
 *   outcome, not a silent one. (Principle 5: never lie about data.)
 * · The server's own validation sentence is what the user sees on
 *   failure. No "Validation failed", no Zod tokens, no status codes.
 * · All five states: default, submitting (button + disabled fields),
 *   error (Alert with recovery text), empty (the form IS the empty
 *   state's action), and — since nothing here can go stale — no stale
 *   state is applicable.
 *
 * Styling is tokens-only via the existing `ferro-field` / `ferro-form`
 * vocabulary; no raw hex, no ad-hoc pixel spacing, 44px touch targets
 * inherited from `.ferro-field__input`. (Principles 6 and 7.)
 *
 * Like CustomerForm, this component does NOT call the API itself — the
 * page owns the fetch and the refetch, so there is exactly one place that
 * knows how a customer's dog list is refreshed.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Button, Modal } from '@ferro/ui';
import type { CreateDogInput } from '../../lib/api.js';

export interface AddDogFormProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Shown in the title so staff can see whose pet they're adding. */
  readonly customerName?: string;
  readonly onSubmit: (input: CreateDogInput) => Promise<void>;
}

interface FormState {
  name: string;
  breed: string;
  sizeCategory: '' | 'small' | 'medium' | 'large' | 'xl';
  color: string;
  alteredStatus: '' | 'intact' | 'spayed' | 'neutered';
  /** #1116 — asked only for a "No": the 7 vs 11 month reminder needs it. */
  sex: '' | 'male' | 'female';
  vetName: string;
  vetPhone: string;
  allergies: string;
  careInstructions: string;
  notes: string;
}

const EMPTY: FormState = {
  name: '',
  breed: '',
  sizeCategory: '',
  color: '',
  alteredStatus: '',
  sex: '',
  vetName: '',
  vetPhone: '',
  allergies: '',
  careInstructions: '',
  notes: '',
};

/**
 * #1116 — the desk's words for the spay/neuter question. Exported so the
 * Playwright spec and the unit test pin the exact copy; "Yes"/"No" leads so
 * the receptionist reads the question Ted asked, with the procedure as the
 * detail. No "altered" anywhere (Ted: owners don't know the word).
 */
export const ADD_DOG_COPY = {
  alteredLabel: 'Spayed or neutered? (required)',
  alteredPlaceholder: 'Choose Yes or No',
  alteredSpayed: 'Yes — spayed (female)',
  alteredNeutered: 'Yes — neutered (male)',
  alteredIntact: 'No — not spayed or neutered',
  alteredHint: 'Required. Ask the owner — the agreement needs altered dogs at six months.',
  alteredMissing: 'Tell us whether the dog is spayed or neutered — Yes or No.',
  sexLabel: 'Male or female?',
  sexHint: 'So the spay/neuter reminder lands at the right age (7 months for a female, 11 for a male).',
} as const;

/** '' → null so we store NULL, never an empty string (see #614/#622). */
function orNull(v: string): string | null {
  const t = v.trim();
  return t.length > 0 ? t : null;
}

/**
 * Mirrors `createDogSchema`'s `.max()` values exactly. The server is still
 * the authority — this only stops the COMMON case from round-tripping to
 * produce an error at all, which is the difference between "the box won't
 * take another character" and "I filled in the form, hit save, and got
 * told off". The realistic trigger is a vet phone with an extension
 * ("303-555-0134 ext. 204" = 22 chars), which is why 20 is here and not
 * left to chance.
 *
 * If a server limit ever changes, the visible symptom is a field that
 * stops one character early — annoying but honest. The reverse (client
 * looser than server) is the one that produces a rejected save, so these
 * must never exceed the schema.
 */
const MAX = {
  name: 100,
  breed: 100,
  color: 100,
  vetName: 200,
  vetPhone: 20,
  allergies: 1000,
  careInstructions: 2000,
  notes: 2000,
} as const;

export function AddDogForm({
  isOpen,
  onClose,
  customerName,
  onSubmit,
}: AddDogFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const alteredRef = useRef<HTMLSelectElement | null>(null);

  // Reopening always starts clean — a half-typed previous dog reappearing
  // is how the wrong breed ends up on the wrong pet at 6:45 AM.
  useEffect(() => {
    if (!isOpen) return;
    setForm(EMPTY);
    setShowMore(false);
    setError(null);
    setSubmitting(false);
    // Straight into the one field that matters; the receptionist should
    // be able to start typing the dog's name without touching anything.
    const t = window.setTimeout(() => nameRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;

    const name = form.name.trim();
    if (!name) {
      setError('Enter the dog’s name to save this pet.');
      nameRef.current?.focus();
      return;
    }
    // #1116 — the form cannot submit without the answer. The server refuses
    // too (createDogSchema); this keeps the refusal one tap away from the
    // field instead of a round trip away.
    if (form.alteredStatus === '') {
      setError(ADD_DOG_COPY.alteredMissing);
      alteredRef.current?.focus();
      return;
    }
    const alteredStatus = form.alteredStatus;

    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        breed: orNull(form.breed),
        sizeCategory: form.sizeCategory === '' ? null : form.sizeCategory,
        color: orNull(form.color),
        alteredStatus,
        // A Yes implies the sex (spayed → female, neutered → male); a No
        // sends what the desk was told, or nothing — never a guess.
        sex:
          alteredStatus === 'spayed'
            ? 'female'
            : alteredStatus === 'neutered'
              ? 'male'
              : form.sex === ''
                ? null
                : form.sex,
        vetName: orNull(form.vetName),
        vetPhone: orNull(form.vetPhone),
        allergies: orNull(form.allergies),
        careInstructions: orNull(form.careInstructions),
        notes: orNull(form.notes),
      });
      onClose();
    } catch (err) {
      // The server's sentence, verbatim — it is the only text that tells
      // the user which field to fix.
      setError(
        err instanceof Error ? err.message : 'Couldn’t add the pet. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customerName ? `Add a pet for ${customerName}` : 'Add a pet'}
      size="md"
    >
      <form className="ferro-form" onSubmit={handleSubmit} noValidate>
        <label className="ferro-field">
          <span className="ferro-field__label">Dog’s name</span>
          <input
            ref={nameRef}
            type="text"
            name="name"
            data-testid="add-dog-name"
            maxLength={MAX.name}
            className="ferro-field__input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={submitting}
            autoComplete="off"
            required
          />
          <span className="ferro-field__hint">
            This and whether the dog is spayed or neutered are all we need
            right now. Everything else can be filled in later.
          </span>
        </label>

        <div className="ferro-form__row">
          <label className="ferro-field">
            <span className="ferro-field__label">Breed</span>
            <input
              type="text"
              name="breed"
              maxLength={MAX.breed}
              className="ferro-field__input"
              value={form.breed}
              onChange={(e) => set('breed', e.target.value)}
              disabled={submitting}
              autoComplete="off"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Size</span>
            <select
              name="sizeCategory"
              className="ferro-field__input"
              value={form.sizeCategory}
              onChange={(e) =>
                set('sizeCategory', e.target.value as FormState['sizeCategory'])
              }
              disabled={submitting}
            >
              <option value="">Not sure</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="xl">Extra large</option>
            </select>
          </label>
        </div>

        <div className="ferro-form__row">
          <label className="ferro-field">
            <span className="ferro-field__label">Colour / markings</span>
            <input
              type="text"
              name="color"
              maxLength={MAX.color}
              className="ferro-field__input"
              value={form.color}
              onChange={(e) => set('color', e.target.value)}
              disabled={submitting}
              autoComplete="off"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">{ADD_DOG_COPY.alteredLabel}</span>
            <select
              ref={alteredRef}
              id="add-dog-altered"
              name="alteredStatus"
              data-testid="add-dog-altered"
              className="ferro-field__input"
              value={form.alteredStatus}
              onChange={(e) =>
                set(
                  'alteredStatus',
                  e.target.value as FormState['alteredStatus'],
                )
              }
              disabled={submitting}
              required
              aria-required="true"
            >
              {/* #1116 — the placeholder is a prompt, not an answer: the form
                  refuses to save it. "Not sure" used to be storable as NULL
                  and the record then carried a question nobody answered. */}
              <option value="">{ADD_DOG_COPY.alteredPlaceholder}</option>
              <option value="spayed">{ADD_DOG_COPY.alteredSpayed}</option>
              <option value="neutered">{ADD_DOG_COPY.alteredNeutered}</option>
              <option value="intact">{ADD_DOG_COPY.alteredIntact}</option>
            </select>
            <span className="ferro-field__hint">{ADD_DOG_COPY.alteredHint}</span>
          </label>
        </div>

        {form.alteredStatus === 'intact' ? (
          <label className="ferro-field" data-testid="add-dog-sex-field">
            <span className="ferro-field__label">{ADD_DOG_COPY.sexLabel}</span>
            <select
              id="add-dog-sex"
              name="sex"
              data-testid="add-dog-sex"
              className="ferro-field__input"
              value={form.sex}
              onChange={(e) => set('sex', e.target.value as FormState['sex'])}
              disabled={submitting}
            >
              <option value="">Not sure</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
            <span className="ferro-field__hint">{ADD_DOG_COPY.sexHint}</span>
          </label>
        ) : null}

        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setShowMore((s) => !s)}
          aria-expanded={showMore}
          data-testid="add-dog-more"
          disabled={submitting}
        >
          {showMore ? 'Hide extra details' : 'Add more details'}
        </button>

        {showMore ? (
          <>
            <p className="ferro-field__hint">
              Nothing below is required. Anything you skip is saved as
              “still needed” on the pet’s file, so it can be finished later.
            </p>
            <div className="ferro-form__row">
              <label className="ferro-field">
                <span className="ferro-field__label">Vet name</span>
                <input
                  type="text"
                  name="vetName"
                  maxLength={MAX.vetName}
                  className="ferro-field__input"
                  value={form.vetName}
                  onChange={(e) => set('vetName', e.target.value)}
                  disabled={submitting}
                  autoComplete="off"
                />
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Vet phone</span>
                <input
                  type="tel"
                  name="vetPhone"
                  maxLength={MAX.vetPhone}
                  className="ferro-field__input"
                  value={form.vetPhone}
                  onChange={(e) => set('vetPhone', e.target.value)}
                  disabled={submitting}
                  autoComplete="off"
                />
              </label>
            </div>
            <label className="ferro-field">
              <span className="ferro-field__label">Allergies</span>
              <input
                type="text"
                name="allergies"
                maxLength={MAX.allergies}
                className="ferro-field__input"
                value={form.allergies}
                onChange={(e) => set('allergies', e.target.value)}
                disabled={submitting}
                autoComplete="off"
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Care instructions</span>
              <textarea
                name="careInstructions"
                maxLength={MAX.careInstructions}
                className="ferro-field__input ferro-field__input--textarea"
                rows={2}
                value={form.careInstructions}
                onChange={(e) => set('careInstructions', e.target.value)}
                disabled={submitting}
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Notes for staff</span>
              <textarea
                name="notes"
                maxLength={MAX.notes}
                className="ferro-field__input ferro-field__input--textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                disabled={submitting}
              />
            </label>
          </>
        ) : null}

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="ferro-form__actions">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save pet'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
